import React from 'react';
import { DollarSign, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';

export const BudgetMonitor = ({ budgets }) => {
  const getStatusColor = (percentage) => {
    if (percentage >= 90) return 'text-destructive';
    if (percentage >= 70) return 'text-warning';
    return 'text-success';
  };
  
  const getStatusIcon = (percentage) => {
    if (percentage >= 90) return <AlertTriangle className="w-4 h-4" />;
    if (percentage >= 70) return <AlertTriangle className="w-4 h-4" />;
    return <CheckCircle2 className="w-4 h-4" />;
  };

  const getServiceLabel = (service) => {
    const labels = {
      'ahrefs': 'AHREFS API',
      'youtube': 'YOUTUBE API',
      'google_suggest': 'GOOGLE SUGGEST',
      'seranking': 'SE RANKING',
    };
    return labels[service] || service.toUpperCase();
  };

  const isFreeService = (service) => service === 'google_suggest';

  const getQuotaMode = (budget) => {
    // LIVE: read from provider API / direct account endpoint
    // EST: derived from internal counters or static plan assumptions
    const source = String(budget?.source || '').toLowerCase();
    if (source === 'ahrefs_api' || source === 'user_api') return 'LIVE';
    if (source) return 'EST';

    if (budget?.service === 'google_suggest') return 'EST';
    if (budget?.service === 'searchapi') return 'EST';
    if (budget?.service === 'youtube') return 'EST';
    if (budget?.service === 'seranking') return 'EST';
    return 'EST';
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {budgets.map((budget) => {
        const pct = budget.percentage_used || 0;
        const isConfigured = budget.configured !== false;
        const usedValue = Number(
          budget.used ??
          budget.total_used ??
          budget.today_used ??
          0
        );
        const limitValue = Number(
          budget.monthly_limit ??
          budget.plan_limit ??
          budget.daily_limit ??
          0
        );
        const remainingValue = Number(budget.remaining ?? budget.daily_remaining ?? 0);
        const daysLeftValue = Number(budget.estimated_days_left ?? 0);
        
        return (
        <Card key={budget.service} className={`card-elevated ${!isConfigured ? 'opacity-50' : ''}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-primary" />
                {getServiceLabel(budget.service)}
              </CardTitle>
              {!isConfigured ? (
                <span className="text-xs text-muted-foreground">NOT SET</span>
              ) : budget.error ? (
                <span className="text-xs text-red-700">Error</span>
              ) : isFreeService(budget.service) ? (
                <span className="flex items-center gap-1 text-sm font-medium text-success">
                  <CheckCircle2 className="w-4 h-4" />
                  FREE
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={`flex items-center gap-1 text-sm font-medium ${getStatusColor(pct)}`}>
                    {getStatusIcon(pct)}
                    {pct.toFixed(1)}%
                  </span>
                  <span
                    className={`text-[11px] leading-none px-1.5 py-1 rounded border ${
                      getQuotaMode(budget) === 'LIVE'
                        ? 'text-emerald-700 border-emerald-300 bg-emerald-50'
                        : 'text-yellow-700 border-yellow-300 bg-yellow-50'
                    }`}
                    title={
                      getQuotaMode(budget) === 'LIVE'
                        ? 'Real provider quota/usage from API'
                        : 'Estimated from internal usage and plan settings'
                    }
                  >
                    {getQuotaMode(budget)}
                  </span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!isConfigured ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Add API key in Settings → Profile</p>
            ) : budget.error ? (
              <p className="text-sm text-red-700 py-4">{budget.error}</p>
            ) : budget.note ? (
              <p className="text-sm text-muted-foreground py-4 text-center">{budget.note}</p>
            ) : (
              <>
                {!isFreeService(budget.service) && (
                  <Progress value={pct} className="h-2 mb-3" />
                )}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{isFreeService(budget.service) ? 'Queries Today' : 'Used'}</p>
                    <p className="font-semibold text-foreground">
                      {usedValue.toLocaleString()}
                    </p>
                  </div>
                  {isFreeService(budget.service) ? (
                    <div>
                      <p className="text-muted-foreground">Total Collected</p>
                      <p className="font-semibold text-foreground">
                        {usedValue.toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-muted-foreground">Remaining</p>
                      <p className="font-semibold text-foreground">
                        {remainingValue.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {!isFreeService(budget.service) && (
                    <>
                      <div>
                        <p className="text-muted-foreground">Limit</p>
                        <p className="font-semibold text-foreground">
                          {limitValue.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Est. Days Left</p>
                        <p className="font-semibold text-foreground">
                          {daysLeftValue} days
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
};
